import { FileImage, RefreshCw } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DrawingManagement, DrawingViewer } from '../components/drawings';
import LoadingSpinner from '../components/LoadingSpinner';
import drawingService from '../services/drawingService';
import { DrawingStats, DrawingStatus, ProjectDrawing } from '../types';

const Drawings: React.FC = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const [stats, setStats] = useState<DrawingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedDrawing, setSelectedDrawing] = useState<ProjectDrawing | null>(null);
  const [showViewer, setShowViewer] = useState(false);

  const isProjectSpecific = !!projectId;

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      const statsData = await drawingService.getDrawingStats(projectId);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load drawing stats:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleDrawingSelect = (drawing: ProjectDrawing) => {
    setSelectedDrawing(drawing);
    setShowViewer(true);
  };

  const handleDrawingDownload = async (drawing: ProjectDrawing) => {
    try {
      await drawingService.downloadDrawing(drawing._id, drawing.file_info.original_name, projectId);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleStatusChange = async (drawing: ProjectDrawing, newStatus: DrawingStatus) => {
    try {
      console.log('Status change:', drawing._id, newStatus);
      await loadStats();
    } catch (error) {
      console.error('Status change failed:', error);
    }
  };

  if (loading) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  return (
    <div className="absolute inset-0 flex flex-col">
      {/* Compact Header Bar */}
      <div className="flex-none bg-white border-b border-gray-200">
        <div className="px-5 py-2.5">
          <div className="flex items-center justify-between">
            {/* Left: Title + Inline Stats */}
            <div className="flex items-center space-x-5">
              <div className="flex items-center space-x-2.5">
                <div className="w-8 h-8 bg-brand-50 rounded-lg flex items-center justify-center flex-none">
                  <FileImage className="h-4 w-4 text-brand-600" />
                </div>
                <div>
                  <h1 className="text-base font-semibold text-gray-900 leading-tight">
                    {isProjectSpecific ? 'Drawings' : 'All Drawings'}
                  </h1>
                  <p className="text-[11px] text-gray-500 leading-tight">
                    {isProjectSpecific ? 'Project drawings' : 'All project drawings'}
                  </p>
                </div>
              </div>

              {/* Inline Stats - Compact */}
              {stats && (
                <div className="hidden lg:flex items-center space-x-3 pl-5 border-l border-gray-200">
                  <div className="flex items-center space-x-1">
                    <span className="text-xs font-semibold text-gray-900">
                      {stats.total_drawings}
                    </span>
                    <span className="text-[10px] text-gray-400">Total</span>
                  </div>
                  {stats.status_breakdown.slice(0, 3).map(status => (
                    <div key={status._id} className="flex items-center space-x-1">
                      <span
                        className={`text-xs font-semibold ${
                          status._id === 'approved'
                            ? 'text-green-600'
                            : status._id === 'under_review'
                              ? 'text-yellow-600'
                              : 'text-gray-600'
                        }`}
                      >
                        {status.count}
                      </span>
                      <span className="text-[10px] text-gray-400 capitalize">
                        {status._id.replace('_', ' ')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right: Actions */}
            <div className="flex items-center space-x-2">
              <button
                onClick={loadStats}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100 transition-colors"
                title="Refresh"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 min-h-0 p-4 bg-gray-50">
        <div className="h-full bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <DrawingManagement
            projectId={projectId}
            showProject={!isProjectSpecific}
            onDrawingSelect={handleDrawingSelect}
          />
        </div>
      </div>

      {/* Drawing Viewer */}
      <DrawingViewer
        drawing={selectedDrawing}
        isOpen={showViewer}
        onClose={() => {
          setShowViewer(false);
          setSelectedDrawing(null);
        }}
        onDownload={selectedDrawing ? () => handleDrawingDownload(selectedDrawing) : undefined}
        onStatusChange={handleStatusChange}
        canEdit={true}
        canApprove={true}
      />
    </div>
  );
};

export default Drawings;
